import { useEffect, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMessage } from "@/lib/api";
import { Loading, ErrorBox, InfoItem, FieldError } from "@/components/common";

export const EmailSection = ({ base = "/platform/email-settings", scopeText = "pour tous les workplaces" }) => {
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [formErr, setFormErr] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get(base).then((r) => setS(r.data)).catch((e) => setError(errMessage(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [base]);

  const save = async () => {
    setBusy("save");
    setFormErr("");
    try {
      const { data } = await api.put(base, { enabled: s.enabled, reply_to: s.reply_to || null });
      setS(data);
      toast.success("Paramètres e-mail enregistrés.");
    } catch (e) { setFormErr(errMessage(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try {
      const { data } = await api.post(`${base}/test`);
      toast.success(`E-mail de test envoyé à ${data.to}.`);
    } catch (e) { toast.error(errMessage(e)); } finally { setBusy(""); }
  };

  if (error) return <ErrorBox message={error} onRetry={load} />;
  if (!s) return <Loading />;
  return (
    <div className="max-w-2xl space-y-5" data-testid="email-settings">
      <div className="rounded-xl border border-border bg-card p-5">
        <p className="flex items-center gap-2 font-semibold"><Mail className="h-5 w-5 text-amber-500" /> Envoi d'e-mails</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <InfoItem label="Service" value={s.provider} testId="email-provider" />
          <InfoItem label="Nom d'expéditeur" value={s.from_name} testId="email-from-name" />
          <InfoItem label="Connexion" value={s.configured ? "Active (aucune clé à saisir)" : "Non configurée"} testId="email-configured" />
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Tous les e-mails Safe Start (comptes, mots de passe, alertes Take 5) utilisent le même modèle graphique, {scopeText}. L'adresse d'envoi est gérée par la plateforme Emergent.</p>
      </div>
      <div className="space-y-4 rounded-xl border border-border bg-card p-5">
        <label className="flex min-h-[44px] items-center gap-3">
          <input type="checkbox" data-testid="email-enabled-checkbox" className="h-5 w-5 accent-amber-500" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} />
          Envoyer les e-mails de réinitialisation de mot de passe
        </label>
        <div>
          <label htmlFor="reply-to" className="text-sm font-semibold">Adresse de réponse (facultatif)</label>
          <Input id="reply-to" data-testid="email-reply-to-input" type="email" placeholder="securite@votre-entreprise.fr" value={s.reply_to || ""} onChange={(e) => setS({ ...s, reply_to: e.target.value })} className="mt-1.5 h-11" />
          <p className="mt-1 text-xs text-muted-foreground">Les réponses des destinataires arriveront sur cette boîte.</p>
        </div>
        <FieldError error={formErr} testId="email-settings-error" />
        <div className="flex flex-wrap gap-2">
          <Button data-testid="email-settings-save" onClick={save} disabled={!!busy}>{busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Enregistrer</Button>
          <Button variant="outline" data-testid="email-test-button" onClick={test} disabled={!!busy}>{busy === "test" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />} M'envoyer un e-mail de test</Button>
        </div>
      </div>
    </div>
  );
};
